import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import plotsImg from '../assets/plots.webp';
import { supabase } from '../supabaseClient';
import './Home.css';

export default function Home() {
  const navigate = useNavigate();
  // Search bar states
  const [activeTab, setActiveTab] = useState('apartments'); 
  const [locationOption, setLocationOption] = useState('All Locations');
  const [typeOption, setTypeOption] = useState('Any Type');
  const [budgetOption, setBudgetOption] = useState('Any Budget');
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [budgetDropdownOpen, setBudgetDropdownOpen] = useState(false);

  // Derived state for dynamic locations and types
  const [availableLocations, setAvailableLocations] = useState(['All Locations']);
  const [availableTypes, setAvailableTypes] = useState(['Any Type']);

  // Card Carousel state
  const [carouselIndex, setCarouselIndex] = useState(0);

  const tabs = [
    { id: 'apartments', label: 'Apartments' },
    { id: 'open_plot', label: 'Open Plot' },
    { id: 'villa_plot', label: 'Villa Plot' },
    { id: 'investment_plot', label: 'Investment Plot' },
    { id: 'premium_plot', label: 'Premium Villa Plot' }
  ];

  const [properties, setProperties] = useState([]);
  const [premiumProperties, setPremiumProperties] = useState([]);
  const [activeHero, setActiveHero] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      // Fetch All Properties for Search Bar
      try {
        const { data: allData } = await supabase.from('properties').select('*');
        if (allData) {
          setProperties(allData);
          setPremiumProperties(allData.filter(p => p.is_premium));
        }
      } catch (e) {
        console.error('Error fetching properties', e);
      }

      // Fetch Active Hero
      try {
        const { data: heroData } = await supabase.from('hero_sections').select('*').eq('active', true).limit(1);
        if (heroData && heroData.length > 0) {
          setActiveHero(heroData[0]);
        }
      } catch (e) {
        console.error('Error fetching hero section', e);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    // Update available locations when active tab or properties change
    if (properties.length > 0) {
      const filterMap = {
        'open_plot': 'open plots',
        'villa_plot': 'villa plots',
        'investment_plot': 'investment plots',
        'premium_plot': 'premium villa plots'
      };

      let filteredProps = [];
      if (activeTab === 'apartments') {
        filteredProps = properties.filter(p => p.category === 'apartments');
      } else {
        const subCat = filterMap[activeTab];
        filteredProps = properties.filter(p => p.category === 'plots' && p.subcategory === subCat);
      }

      const locations = new Set(filteredProps.map(p => p.location).filter(Boolean));
      setAvailableLocations(['All Locations', ...Array.from(locations)]);
      setLocationOption('All Locations'); // Reset on tab change

      const sizes = new Set(filteredProps.map(p => p.sizes).filter(Boolean));
      setAvailableTypes(['Any Type', ...Array.from(sizes)]);
      setTypeOption('Any Type');
    }
  }, [activeTab, properties]);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (activeTab === 'apartments') {
      navigate('/apartments', { state: { type: typeOption, budget: budgetOption, location: locationOption } });
    } else {
      // Map activeTab to Plots filter
      const filterMap = {
        'open_plot': 'open plots',
        'villa_plot': 'villa plots',
        'investment_plot': 'investment plots',
        'premium_plot': 'premium villa plots'
      };
      navigate('/plots', { state: { filter: filterMap[activeTab] || 'all', budget: budgetOption, location: locationOption, type: typeOption } });
    }
  };

  // Carousel navigators
  const nextSlide = () => {
    setCarouselIndex((prev) => (prev + 1) % (premiumProperties.length || 1));
  };

  const prevSlide = () => {
    setCarouselIndex((prev) => (prev - 1 + premiumProperties.length) % (premiumProperties.length || 1));
  };

  return (
    <div className="home-page animate-fade-in-up">
      {/* Premium Hero Section */}
      <section 
        className="hero-section" 
        style={activeHero?.image_url ? { backgroundImage: `url(${activeHero.image_url})` } : {}}
      >
        <div className="hero-top-overlay"></div>
        <div className="hero-overlay"></div>
        
        <div className="hero-content-wrapper container">
          {/* Centered Hero Text */}
          <div className="hero-right-banner animate-fade-in-up">
            <h1 className="banner-title">
              {activeHero?.title || 'Redefining The Living Luxury'}
            </h1>
            
            <button className="banner-cta-btn" onClick={() => navigate('/apartments')}>
              Explore Now <span className="arrow">→</span>
            </button>
          </div>
        </div>

      </section>

      {/* Floating Search Container - Extracted as a separate section */}
      <div className="search-section-wrapper">
        <div className="search-floating-card">
          <div className="search-tabs">
            {tabs.map((tab) => (
              <button 
                key={tab.id}
                type="button"
                className={`search-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabClick(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <form className="search-bar-row-99" onSubmit={handleSearchSubmit}>
            {/* Left Location Column (Dynamic) */}
            <div 
              className="search-col-99 dropdown-col-99"
              onMouseEnter={() => setLocationDropdownOpen(true)}
              onMouseLeave={() => setLocationDropdownOpen(false)}
            >
              <span className="icon-99">📍</span>
              <button 
                type="button" 
                className="dropdown-btn-99"
                onClick={() => setLocationDropdownOpen(!locationDropdownOpen)}
              >
                <div className="dropdown-text-wrap" style={{ maxWidth: '180px', overflow: 'hidden' }}>
                  <span className="label-99">Location</span>
                  <span className="val-99" style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{locationOption}</span>
                </div>
                <span className="chevron-99">▼</span>
              </button>
              {locationDropdownOpen && (
                <div className="dropdown-menu-99" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {availableLocations.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className="dropdown-opt-99"
                      onClick={() => {
                        setLocationOption(opt);
                        setLocationDropdownOpen(false);
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="divider-line-99"></div>

            {/* Middle Type Column: Show for all tabs */}
            <div 
              className="search-col-99 dropdown-col-99"
              onMouseEnter={() => setTypeDropdownOpen(true)}
              onMouseLeave={() => setTypeDropdownOpen(false)}
            >
              <span className="icon-99 react-icon-wrapper-99">
                <svg className="react-home-svg" viewBox="0 0 24 24" fill="none" stroke="#0d7a87" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '18px', height: '18px', display: 'block' }}>
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              </span>
              <button 
                type="button" 
                className="dropdown-btn-99"
                onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
              >
                <div className="dropdown-text-wrap" style={{ maxWidth: '180px', overflow: 'hidden' }}>
                  <span className="label-99">Type / Size</span>
                  <span className="val-99" style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{typeOption}</span>
                </div>
                <span className="chevron-99">▼</span>
              </button>
              {typeDropdownOpen && (
                <div className="dropdown-menu-99" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {availableTypes.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className="dropdown-opt-99"
                      onClick={() => {
                        setTypeOption(opt);
                        setTypeDropdownOpen(false);
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="divider-line-99"></div>

            {/* Right Budget Column with Hover/Click options */}
            <div 
              className="search-col-99 dropdown-col-99"
              onMouseEnter={() => setBudgetDropdownOpen(true)}
              onMouseLeave={() => setBudgetDropdownOpen(false)}
            >
              <span className="icon-99">₹</span>
              <button 
                type="button" 
                className="dropdown-btn-99"
                onClick={() => setBudgetDropdownOpen(!budgetDropdownOpen)}
              >
                <div className="dropdown-text-wrap" style={{ maxWidth: '180px', overflow: 'hidden' }}>
                  <span className="label-99">Budget</span>
                  <span className="val-99" style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{budgetOption}</span>
                </div>
                <span className="chevron-99">▼</span>
              </button>
              {budgetDropdownOpen && (
                <div className="dropdown-menu-99" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {['Any Budget', 'Under ₹50 Lakhs', '₹50 Lakhs - ₹1 Crore', '₹1 Crore - ₹2 Crores', 'Above ₹2 Crores'].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className="dropdown-opt-99"
                      onClick={() => {
                        setBudgetOption(opt);
                        setBudgetDropdownOpen(false);
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Premium search execute button */}
            <button type="submit" className="search-submit-btn-99">
              <svg className="search-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: '16px', height: '16px' }}>
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <span>Search Properties</span>
            </button>
          </form>
        </div>
      </div>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container stats-container">
          <div className="stat-card">
            <h3>5+</h3>
            <p>Years of Legacy</p>
          </div>
          <div className="stat-card">
            <h3>10+</h3>
            <p>Premium Projects</p>
          </div>
          <div className="stat-card">
            <h3>100+</h3>
            <p>Happy Families</p>
          </div>
          <div className="stat-card">
            <h3>100%</h3>
            <p>On-Time Delivery</p>
          </div>
        </div>
      </section>

      {/* Intro Section (About Skyon) */}
      <section className="intro-section">
        <div className="container grid-2">
          <div className="intro-text">
            <span className="section-tag">About Skyon</span>
            <h2 className="section-title">Building Trust, Delivering <span>Excellence</span></h2>
            <p className="intro-paragraph">
              At Skyon Infra Developers, we believe that development is not just about concrete and steel. It is about creating vibrant communities, luxury residences, and investment opportunities that stand the test of time.
            </p>
            <p className="intro-paragraph">
              Derived from our core values and styled after our identity, we bring sustainable green ecosystems and modern layouts together. Whether you are looking for a sky high luxury apartment or a secure, gated plot, Skyon is your partner in building the future.
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/about')} style={{ marginTop: '20px' }}>
              Learn More About Us
            </button>
          </div>
          <div className="intro-visual">
            <div className="visual-card animate-float">
              <img src={plotsImg} alt="Skyon Premium Development" className="intro-image" />
              <div className="visual-badge">
                <span className="badge-number">100%</span>
                <span className="badge-text">Quality Verified</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Properties Unique Carousel Section */}
      <section className="our-properties-section">
        <div className="container">
          <div className="text-center">
            <span className="section-tag">Featured Ventures</span>
            <h2 className="section-title">Our Premium <span>Properties</span></h2>
            <p className="section-desc">
              Slide through our active portfolio of luxury homes and strategic plotted developments. Hover over a card to view specs and layouts.
            </p>
          </div>

          {/* Centered Focus Sliding Carousel */}
          <div className="unique-carousel-container flex-center">
            {/* Left Prev Arrow Button */}
            <button className="carousel-arrow-btn prev" onClick={prevSlide} aria-label="Previous property">
              ‹
            </button>

            <div className="unique-carousel-track-wrapper">
              <div 
                className="unique-carousel-track" 
                style={{ 
                  transform: `translateX(calc(-140px - ${carouselIndex * 310}px))` 
                }}
              >
                {premiumProperties.map((prop, idx) => {
                  const isActive = idx === carouselIndex;
                  return (
                    <div 
                      key={prop.id}
                      className={`carousel-card ${isActive ? 'active' : 'side'}`} 
                      onClick={() => setCarouselIndex(idx)}
                    >
                      <div className="card-image-wrap">
                        <img src={prop.image_url || plotsImg} alt={prop.title} />
                        
                        {/* Hover details sliding overlay for active card */}
                        {isActive && (
                          <div className="card-hover-overlay">
                            <span className="overlay-location">📍 {prop.location}</span>
                            <p className="overlay-desc">{prop.tagline}</p>
                            
                            <div className="overlay-specs-grid">
                              <div className="overlay-spec-col">
                                <span className="spec-lbl">Sizes</span>
                                <span className="spec-val">{prop.sizes}</span>
                              </div>
                              <div className="overlay-spec-col">
                                <span className="spec-lbl">Status</span>
                                <span className="spec-val">{prop.status}</span>
                              </div>
                              <div className="overlay-spec-col">
                                <span className="spec-lbl">Category</span>
                                <span className="spec-val">{prop.category}</span>
                              </div>
                            </div>

                            <button 
                              className="btn btn-secondary overlay-details-btn"
                              onClick={(e) => {
                                e.stopPropagation(); // Avoid triggering card click
                                window.localStorage.setItem('enquiry_subject', `Enquiry about ${prop.title}`);
                                navigate(prop.category === 'apartments' ? '/apartments' : '/plots');
                              }}
                            >
                              View Details
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="card-name-bar">
                        <span className="card-tag-name">@{prop.title?.replace(/\s+/g, '').toLowerCase()}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Next Arrow Button */}
            <button className="carousel-arrow-btn next" onClick={nextSlide} aria-label="Next property">
              ›
            </button>
          </div>
        </div>
      </section>

      {/* Pillars Section */}
      <section className="pillars-section">
        <div className="container">
          <div className="text-center">
            <span className="section-tag">Our Pillars</span>
            <h2 className="section-title">The Skyon <span>Promise</span></h2>
            <p className="section-desc">
              Every project we design and construct is guided by our foundational design and delivery rules.
            </p>
          </div>
          <div className="grid-3">
            <div className="premium-card competency-card">
              <div className="card-icon teal-icon">✦</div>
              <h3>Architectural Mastery</h3>
              <p>Collaborating with elite architects to create visually striking, spacious, and highly functional designs.</p>
            </div>
            <div className="premium-card competency-card">
              <div className="card-icon orange-icon">✦</div>
              <h3>Sustainable Luxury</h3>
              <p>Integrating eco friendly elements, green landscaping, water harvesting, and solar power into our communities.</p>
            </div>
            <div className="premium-card competency-card">
              <div className="card-icon green-icon">✦</div>
              <h3>Uncompromised Legality</h3>
              <p>100% transparent documentation, RERA approvals, clear titles, and seamless customer registration procedures.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured CTA Banner */}
      <section className="featured-cta-banner">
        <div className="container cta-banner-content text-center">
          <h2>Ready to Secure Your Future Home?</h2>
          <p>Explore our premium active listings in prime areas or schedule a site visit today.</p>
          <div className="cta-banner-buttons">
            <button className="btn btn-accent" onClick={() => navigate('/apartments')}>
              Browse Apartments
            </button>
            <button className="btn btn-secondary" onClick={() => navigate('/plots')} style={{ color: 'white', borderColor: 'white' }}>
              Browse Plots
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
